import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Field, fieldInputCls } from "@/components/Field";
import { FileField } from "@/components/FileField";
import { PageHeader } from "@/pages/Events";
import { useSettings } from "@/context/SettingsContext";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { EVENT_TYPES } from "@/lib/format";
import { TyreTargetsEditor, cleanTargets } from "@/components/TyreTargetsEditor";

const Block = ({ title, hint, children, testid }) => (
  <section data-testid={testid} className="bg-[#12141C] border border-[#282D3D] rounded-sm p-5 space-y-4">
    <div>
      <h2 className="font-heading uppercase font-bold text-xl flex items-center gap-2"><span className="w-1 h-4 bg-[#FF3B30]" />{title}</h2>
      {hint && <p className="font-mono text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
    {children}
  </section>
);

export default function Settings() {
  const { t } = useT();
  const { settings, setSettings, loaded } = useSettings();
  const [form, setForm] = useState(settings);
  const [channels, setChannels] = useState([]);
  useEffect(() => {
    api.get("/telemetry/channels").then((r) => setChannels(r.data));
  }, []);
  useEffect(() => {
    if (loaded) {
      setForm(settings);
    }
  }, [settings, loaded]);

  const toggleType = (ty) => {
    const hidden = form.hidden_event_types.includes(ty) ? form.hidden_event_types.filter((x) => x !== ty) : [...form.hidden_event_types, ty];
    setForm({ ...form, hidden_event_types: hidden });
  };
  const defCh = form.default_channels || [];
  const toggleChannel = (c) => setForm({ ...form, default_channels: defCh.includes(c) ? defCh.filter((x) => x !== c) : [...defCh, c] });
  const allChannels = [...new Set([...channels, ...defCh])];

  const save = async () => {
    try {
      const { data } = await api.put("/settings", { ...form, tyre_targets: cleanTargets(form.tyre_targets) });
      setSettings(data);
      toast.success(t("saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <PageHeader title={t("settings_title")} sub={t("settings_sub")} action={
        <Button data-testid="settings-save-btn" onClick={save} disabled={!loaded} className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-lg h-11 px-6">
          <Save className="w-4 h-4 mr-2" /> {t("save")}
        </Button>
      } />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Block title={t("team")} testid="settings-team">
          <Field label={t("team_name")}>
            <Input data-testid="settings-team-name-input" value={form.team_name} onChange={(e) => setForm({ ...form, team_name: e.target.value })} className={fieldInputCls} />
          </Field>
          <Field label={t("team_logo")}>
            <FileField testid="settings-logo" preview accept="image/jpeg,image/png,image/webp,image/gif" value={form.team_logo} onChange={(v) => setForm({ ...form, team_logo: v })} />
          </Field>
        </Block>
        <Block title={t("team_notes")} testid="settings-notes">
          <Textarea data-testid="settings-team-notes-input" rows={6} value={form.team_notes} onChange={(e) => setForm({ ...form, team_notes: e.target.value })} className={`${fieldInputCls} h-auto font-sans`} />
        </Block>
        <Block title={t("hidden_event_types")} hint={t("hidden_types_hint")} testid="settings-types">
          <div className="flex flex-wrap gap-2">
            {EVENT_TYPES.map((ty) => {
              const hidden = form.hidden_event_types.includes(ty);
              return (
                <button type="button" key={ty} data-testid={`settings-type-${ty}`} data-hidden={hidden ? "true" : "false"} onClick={() => toggleType(ty)}
                  className={`px-3 py-1.5 border rounded-sm font-mono text-xs uppercase transition-colors duration-200 ${hidden ? "border-[#282D3D] text-slate-600 line-through" : "border-[#10B981]/50 text-[#10B981]"}`}>
                  {t(`type_${ty}`)}
                </button>
              );
            })}
          </div>
        </Block>
        <Block title={t("hide_categories")} hint={t("hide_categories_hint")} testid="settings-categories">
          <label className="flex items-center gap-3 cursor-pointer w-fit">
            <Switch data-testid="settings-hide-categories-switch" checked={form.hide_categories} onCheckedChange={(v) => setForm({ ...form, hide_categories: v })} className="data-[state=checked]:bg-[#FFB800]" />
            <span className="font-mono text-xs uppercase text-slate-300">{t("hide_categories")}</span>
          </label>
        </Block>
        <div className="lg:col-span-2">
          <Block title={t("default_channels")} hint={t("default_channels_hint")} testid="settings-default-channels">
            {allChannels.length === 0 && <p className="font-mono text-xs text-slate-500">{t("no_channels_yet")}</p>}
            <div className="flex flex-wrap gap-2">
              {allChannels.map((c) => {
                const on = defCh.includes(c);
                return (
                  <button type="button" key={c} data-testid={`settings-channel-${c}`} data-active={on ? "true" : "false"} onClick={() => toggleChannel(c)}
                    className={`px-3 py-1.5 border rounded-sm font-mono text-xs uppercase transition-colors duration-200 ${on ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-[#282D3D] text-slate-400 hover:text-white"}`}>
                    {on && <span className="mr-1 text-[10px]">{defCh.indexOf(c) + 1}.</span>}{c}
                  </button>
                );
              })}
            </div>
          </Block>
        </div>
        <div className="lg:col-span-2">
          <Block title={t("tyre_targets")} hint={t("tyre_targets_hint")} testid="settings-tyre-targets">
            <TyreTargetsEditor value={form.tyre_targets} onChange={(v) => setForm({ ...form, tyre_targets: v })} />
          </Block>
        </div>
      </div>
    </div>
  );
}
